import React, { type FunctionComponent, useMemo } from 'react'
import { useRetailerId } from '@retailer-platform/dashboard/routing'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { groupBy } from 'lodash'
import styled from '@emotion/styled'
import { useStoreConfigurations } from '@retailer-platform/dashboard/api'
import type { WhitelabelSiteData } from '../utils/Onboarding.types.ts'
import { useGetWhitelabelSites } from '../../utils/useGetWhitelabelSites.hook.ts'
import { StoreConfigurationRelationshipEnum } from '../../api'
import { OnboardingSiteSetupChecklist } from './components/checklist/OnboardingSiteSetupChecklist.tsx'

type OnboardingSiteSetupChecklistGroupProps = {}

const FlexContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  margin: '20px',
})

const FlexItem = styled.div({
  boxSizing: 'border-box',
  margin: '10px',

  '@media screen and (max-width: 1024px) ': {
    flex: '0 0 100%', // 1 item per row on smaller screen
  },

  '@media screen and (min-width: 1024px) ': {
    flex: '0 0 calc(50% - 20px)', // 2 items per row
  },
})

export const OnboardingSiteSetupChecklistGroup: FunctionComponent<
  OnboardingSiteSetupChecklistGroupProps
> = () => {
  const { warehouses: availableWarehouses, partnerId } = usePartnerContext()
  const warehouseId = useRetailerId()

  const isBannerSelected = useMemo(() => warehouseId && warehouseId !== '0', [warehouseId])

  // if banner is selected, only get the store configurations for the banner, otherwise get all store configurations for the partner
  const applicableRetailerIds = useMemo(
    () => (isBannerSelected ? [warehouseId] : availableWarehouses?.map(e => e.id.toString()) ?? []),
    [warehouseId, availableWarehouses, isBannerSelected]
  )

  const storeConfigurationsResult = useStoreConfigurations({
    retailerIds: applicableRetailerIds,
    storeConfigRelationship: StoreConfigurationRelationshipEnum.Associated,
  })

  const storeConfigurations = useMemo(
    () => storeConfigurationsResult?.data?.storeConfigurationsByRetailerIds ?? [],
    [storeConfigurationsResult?.data?.storeConfigurationsByRetailerIds]
  )

  /* Get Onboarding Whitelabel Sites by partner */
  const { apiResult: whitelabelSites } = useGetWhitelabelSites({
    partnerId,
    ...(isBannerSelected && { warehouseId }), // send warehouseId only if banner is selected
  })

  const sitesGroupedByScId = useMemo(
    () => groupBy(whitelabelSites, 'storeConfigurationId'),
    [whitelabelSites]
  )

  const renderChecklistItems = useMemo(
    () =>
      Object.entries(sitesGroupedByScId).map(([storeConfigurationId, sites]) => (
        <FlexItem key={storeConfigurationId}>
          <OnboardingSiteSetupChecklist
            sites={sites as WhitelabelSiteData[]}
            storeConfigurationName={
              storeConfigurations.find(sc => sc.id === storeConfigurationId)?.brandedName
            }
          />
        </FlexItem>
      )),
    [sitesGroupedByScId, storeConfigurations]
  )

  return <FlexContainer>{renderChecklistItems}</FlexContainer>
}
